import { useEffect, useMemo, useRef, useState } from "react";
import { getPriests, sendToPriests } from "@/lib/api";
import { X, Mail, Search, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { CaptchaWidget } from "@/components/public/CaptchaWidget";
import { useT } from "@/lib/t";

/**
 * SendToPriestModal — one-click bulletin-to-pulpit distribution.
 * Parish staffer picks up to 10 priests from a searchable directory; the CCC
 * backend sends each of them the Homily Aid + Bulletin Insert PDFs pre-attached,
 * with reply-to set to the sender.
 *
 * Localized via ``useT`` — every visible string reads from ``send_priest.*``
 * so a bilingual parish office in Denver or Colorado Springs can drive the
 * modal in Spanish.
 */
export function SendToPriestModal({ onClose, alertSlug, alertTitle }) {
  const t = useT();
  const [priests, setPriests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [diocese, setDiocese] = useState("");
  const [selected, setSelected] = useState({});
  const [form, setForm] = useState({ sender_name: "", sender_email: "", note: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null); // {priests_targeted, sent}
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    getPriests().then(setPriests).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const lower = q.trim().toLowerCase();
    return priests.filter(p => {
      if (diocese && p.diocese !== diocese) return false;
      if (!lower) return true;
      return (p.name || "").toLowerCase().includes(lower)
        || (p.parish || "").toLowerCase().includes(lower)
        || (p.city || "").toLowerCase().includes(lower);
    });
  }, [priests, q, diocese]);

  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);

  const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));

  const submit = async (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) return toast.error(t("send_priest.choose_priests"));
    if (selectedIds.length > 10) return toast.error(t("send_priest.max_10_hint"));
    if (!form.sender_name.trim() || !form.sender_email.trim()) return toast.error(`${t("send_priest.sender_name")} + ${t("send_priest.sender_email")}`);
    if (captchaRef.current?.isEnabled() && !captchaToken) return toast.error(t("misc.captcha_required"));
    setSending(true);
    try {
      const r = await sendToPriests(alertSlug, {
        priest_ids: selectedIds,
        sender_name: form.sender_name,
        sender_email: form.sender_email,
        note: form.note || null,
      }, captchaToken);
      setDone({ priests_targeted: r.priests_targeted, sent: r.sent });
      toast.success(t("send_priest.thanks"));
    } catch (err) {
      const msg = err?.response?.data?.detail || t("send_priest.generic_fail");
      toast.error(msg);
      captchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setSelected({});
    setForm({ sender_name: "", sender_email: "", note: "" });
    setDone(null);
    setQ("");
    setDiocese("");
  };

  const closeAndReset = () => {
    onClose();
  };

  if (!alertSlug) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="send-to-priest-title"
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="send-to-priest-modal"
      onClick={closeAndReset}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-ccc-plum-deep p-5 bg-ccc-plum text-ccc-paper">
          <div>
            <div className="eyebrow eyebrow-light">Parish Toolkit</div>
            <h3 id="send-to-priest-title" className="font-serif text-2xl mt-1">{t("send_priest.title")}</h3>
            <div className="text-ccc-paper/80 mt-1" style={{ fontSize: "14px" }}>{t("send_priest.description")}</div>
          </div>
          <button
            onClick={closeAndReset}
            aria-label="Close"
            data-testid="close-send-to-priest"
            className="text-ccc-paper hover:text-ccc-gold-light p-1"
          >
            <X size={20} />
          </button>
        </div>

        {done ? (
          <div className="p-10 text-center" data-testid="send-to-priest-success">
            <CheckCircle size={44} className="text-ccc-plum mx-auto" />
            <h4 className="font-serif text-2xl text-ccc-plum mt-3">{t("send_priest.thanks")}</h4>
            <div className="text-ccc-muted mt-2 italic" style={{ fontSize: "13px" }}>"{alertTitle}"</div>
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={reset}
                data-testid="send-to-priest-send-again"
                className="border border-ccc-plum text-ccc-plum hover:bg-ccc-plum hover:text-ccc-paper px-4 py-2 uppercase tracking-[0.08em]"
                style={{ fontSize: "13px" }}
              >
                {t("send_priest.choose_priests")}
              </button>
              <button
                onClick={closeAndReset}
                data-testid="send-to-priest-done"
                className="bg-ccc-plum text-ccc-paper hover:bg-ccc-plum-deep px-4 py-2 uppercase tracking-[0.08em]"
                style={{ fontSize: "13px" }}
              >
                {t("common.back")}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col flex-1 overflow-hidden">
            {/* Priest search + list */}
            <div className="p-5 border-b border-ccc-rule">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-3 top-3 text-ccc-muted" />
                  <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={t("send_priest.choose_priests")}
                    data-testid="priest-search"
                    className="w-full pl-9 pr-3 py-2 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
                    style={{ fontSize: "15px" }}
                  />
                </div>
                <select
                  value={diocese}
                  onChange={(e) => setDiocese(e.target.value)}
                  data-testid="priest-diocese"
                  className="px-3 py-2 border border-ccc-rule focus:outline-none focus:border-ccc-plum bg-white"
                  style={{ fontSize: "15px" }}
                >
                  <option value="">Denver / CO Springs / Pueblo</option>
                  <option value="Archdiocese of Denver">Denver</option>
                  <option value="Diocese of Colorado Springs">Colorado Springs</option>
                  <option value="Diocese of Pueblo">Pueblo</option>
                </select>
              </div>
              <div className="text-ccc-muted mt-2" style={{ fontSize: "13px" }}>
                {selectedIds.length}/10 &middot; {filtered.length}
              </div>
            </div>

            <div className="overflow-y-auto flex-1 px-5 py-3" data-testid="priest-list">
              {loading ? (
                <div className="text-ccc-muted" style={{ fontSize: "15px" }}>{t("common.loading")}</div>
              ) : filtered.length === 0 ? (
                <div className="text-ccc-muted" style={{ fontSize: "15px" }}>{t("common.not_found")}</div>
              ) : (
                <ul className="divide-y divide-ccc-rule">
                  {filtered.map((p) => {
                    const checked = !!selected[p.id];
                    const disabled = !checked && selectedIds.length >= 10;
                    return (
                      <li key={p.id}>
                        <label
                          className={`flex items-start gap-3 py-3 cursor-pointer ${disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-ccc-paper"}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={() => toggle(p.id)}
                            data-testid={`priest-check-${p.id}`}
                            className="mt-1 accent-ccc-plum"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-ccc-plum" style={{ fontSize: "16px" }}>{p.name}</div>
                            <div className="text-ccc-body" style={{ fontSize: "14px" }}>{p.parish}</div>
                            <div className="text-ccc-muted" style={{ fontSize: "13px" }}>{p.city} &middot; {p.diocese}</div>
                          </div>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="border-t border-ccc-rule p-5 bg-ccc-paper">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <input
                  required
                  type="text"
                  value={form.sender_name}
                  onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
                  placeholder={t("send_priest.sender_name")}
                  data-testid="priest-sender-name"
                  className="px-3 py-2 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
                  style={{ fontSize: "15px" }}
                />
                <input
                  required
                  type="email"
                  value={form.sender_email}
                  onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
                  placeholder={t("send_priest.sender_email")}
                  data-testid="priest-sender-email"
                  className="px-3 py-2 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
                  style={{ fontSize: "15px" }}
                />
              </div>
              <textarea
                rows={3}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder={t("send_priest.note_placeholder")}
                data-testid="priest-note"
                className="w-full px-3 py-2 border border-ccc-rule focus:outline-none focus:border-ccc-plum font-serif"
                style={{ fontSize: "15px" }}
              />
              <CaptchaWidget ref={captchaRef} onToken={setCaptchaToken} />
              <div className="flex items-center justify-end mt-4">
                <button
                  type="submit"
                  disabled={sending || selectedIds.length === 0}
                  data-testid="priest-submit"
                  className="inline-flex items-center gap-2 bg-ccc-plum hover:bg-ccc-plum-deep disabled:opacity-50 disabled:cursor-not-allowed text-ccc-paper px-5 py-2 font-semibold uppercase tracking-[0.08em]"
                  style={{ fontSize: "14px" }}
                >
                  <Mail size={14} />
                  {sending ? t("send_priest.submitting") : t("send_priest.submit")}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
